// Speaker toggle in the top bar: mute/unmute the spoken alert.
//
// It also surfaces the browser's autoplay lock. Until the page receives a real
// user gesture, browsers block ALL audio — so an alert arriving on a freshly
// loaded page is silent and the operator has no idea why. When that's the case
// we show an amber dot, and clicking it ENABLES sound (primes the audio context)
// rather than toggling mute — clicking a "turn sound on" hint must never be the
// thing that turns sound off.

import React, { useEffect, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { isMuted, setMuted, subscribeMute, isAudioLocked, primeAudio } from '../utils/alertSound';

export default function SoundToggle() {
  const [muted, setMutedState] = useState(isMuted());
  const [locked, setLocked] = useState(false);

  useEffect(() => subscribeMute(setMutedState), []);

  // Re-check after any interaction so the hint clears itself once it's moot.
  useEffect(() => {
    const check = () => setLocked(isAudioLocked());
    check();
    const t = setTimeout(check, 500);
    window.addEventListener('click', check);
    window.addEventListener('keydown', check);
    return () => {
      clearTimeout(t);
      window.removeEventListener('click', check);
      window.removeEventListener('keydown', check);
    };
  }, []);

  const needsGesture = !muted && locked;
  const title = muted
    ? 'Alert sound off — click to enable'
    : needsGesture
      ? 'Click to enable alert sound (your browser blocks audio until you interact with the page)'
      : 'Alert sound on — click to mute';

  const onClick = () => {
    if (needsGesture) {
      primeAudio();
      setTimeout(() => setLocked(isAudioLocked()), 150);
      return;
    }
    setMuted(!muted);
  };

  return (
    <button
      type="button"
      className="text-gray-400 hover:text-white relative"
      title={title}
      aria-label={title}
      onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer' }}
    >
      {muted ? <VolumeX size={22} /> : <Volume2 size={22} />}
      {needsGesture && (
        <span
          className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full"
          style={{ background: '#f59e0b' }}
        />
      )}
    </button>
  );
}
